<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { ago } from '../lib/format'
import avatarUrl from '../images/avatar.webp'

// "Nyt fra bageriet": unseen posts pop in as notifications, the avatar ring shows what is unread,
// and the feed drawer lists everything. Seen/notified state lives in the visitor's browser.
const props = defineProps({ items: { type: Array, default: () => [] } })

const SEEN_KEY = 'dsk:last-seen-announcement'
const NOTIFIED_KEY = 'dsk:notified-announcements'
const TOAST_MS = 7000

const storage = {
  get(key, fallback) {
    try {
      return JSON.parse(localStorage.getItem(key)) ?? fallback
    } catch {
      return fallback
    }
  },
  set(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value))
    } catch {
      // Private mode or blocked storage: notifications just show again next visit.
    }
  },
}

const seenId = ref(storage.get(SEEN_KEY, 0))
const notified = new Set(storage.get(NOTIFIED_KEY, []))
const now = ref(Date.now())

const unread = computed(() => props.items.filter((item) => item.id > seenId.value))

// ---- Notifications ----
const queue = []
const toast = ref(null)
const toastPaused = ref(false)
let toastTimer = null
let toastStartedAt = 0
let toastRemaining = TOAST_MS

function enqueueUnseen() {
  if (drawerOpen.value) return
  const fresh = unread.value.filter((item) => !notified.has(item.id) && !queue.includes(item)).slice(0, 2)
  queue.push(...fresh.reverse()) // oldest first, so the newest ends up as the last word
  showNext()
}

function showNext() {
  if (toast.value || queue.length === 0) return
  toast.value = queue.shift()
  notified.add(toast.value.id)
  storage.set(NOTIFIED_KEY, [...notified].slice(-50))
  ring()
  toastRemaining = TOAST_MS
  startTimer()
}

function startTimer() {
  clearTimeout(toastTimer)
  toastStartedAt = Date.now()
  toastTimer = setTimeout(dismissToast, toastRemaining)
}

function pauseToast() {
  if (!toast.value || toastPaused.value) return
  toastPaused.value = true
  clearTimeout(toastTimer)
  toastRemaining -= Date.now() - toastStartedAt
}

function resumeToast() {
  if (!toast.value || !toastPaused.value) return
  toastPaused.value = false
  startTimer()
}

function dismissToast() {
  clearTimeout(toastTimer)
  toast.value = null
  toastPaused.value = false
  setTimeout(showNext, 450)
}

// ---- Trigger: avatar with a live ring + a speech bubble that cycles through titles ----
const ringing = ref(false)
function ring() {
  ringing.value = false
  requestAnimationFrame(() => (ringing.value = true))
}

const tickerIndex = ref(0)
// Only posts marked "Vis i taleboblen" in the admin cycle here; unread ones first.
const tickerItems = computed(() => {
  const featured = props.items.filter((item) => item.featured)
  return [...featured.filter((item) => item.id > seenId.value), ...featured.filter((item) => item.id <= seenId.value)]
})
const tickerItem = computed(() => tickerItems.value[tickerIndex.value % tickerItems.value.length])
let ticker

// ---- Feed drawer ----
const drawerOpen = ref(false)
const highlightId = ref(null)
const unreadWhenOpened = ref(new Set())
const trigger = ref(null)
const closeButton = ref(null)

async function openFeed(focusId = null) {
  queue.length = 0
  if (toast.value) dismissToast()
  unreadWhenOpened.value = new Set(unread.value.map((item) => item.id))
  highlightId.value = focusId
  drawerOpen.value = true
  seenId.value = Math.max(0, ...props.items.map((item) => item.id))
  storage.set(SEEN_KEY, seenId.value)
  await nextTick()
  closeButton.value?.focus()
  if (focusId) document.getElementById(`feed-${focusId}`)?.scrollIntoView({ block: 'nearest' })
}

function closeFeed() {
  drawerOpen.value = false
  trigger.value?.focus()
}

watch(drawerOpen, (open) => document.documentElement.classList.toggle('drawer-open', open))
// New posts arriving while the page is open (the storefront refreshes in the background).
watch(() => props.items.map((item) => item.id).join(), () => enqueueUnseen())

let clock
let firstNotification
const onKey = (event) => event.key === 'Escape' && drawerOpen.value && closeFeed()
onMounted(() => {
  firstNotification = setTimeout(enqueueUnseen, 1600)
  clock = setInterval(() => (now.value = Date.now()), 60_000)
  ticker = setInterval(() => !drawerOpen.value && tickerIndex.value++, 4500)
  document.addEventListener('keydown', onKey)
})
onBeforeUnmount(() => {
  clearTimeout(firstNotification)
  clearTimeout(toastTimer)
  clearInterval(clock)
  clearInterval(ticker)
  document.removeEventListener('keydown', onKey)
  document.documentElement.classList.remove('drawer-open')
})
</script>

<template>
  <div v-if="items.length" class="news">
    <button
      ref="trigger"
      type="button"
      class="story"
      :class="{ 'has-unread': unread.length, 'is-ringing': ringing }"
      :aria-label="unread.length ? `Nyt fra bageriet – ${unread.length} ulæste` : 'Nyt fra bageriet'"
      :aria-expanded="drawerOpen"
      @click="openFeed()"
      @animationend="ringing = false"
    >
      <span class="story__ring" aria-hidden="true"><img class="avatar" :src="avatarUrl" alt="" width="192" height="192" /></span>
      <span v-if="tickerItem" class="story__bubble" aria-hidden="true">
        <!-- Invisible copies of every title in the rotation, stacked in the same cell: the bubble
             takes the widest one's width and keeps it, so it doesn't jump as titles change. -->
        <span v-for="item in tickerItems" :key="`size-${item.id}`" class="story__line story__line--sizer">
          <span class="story__kind">{{ item.kindLabel }}</span>{{ item.title }}
        </span>
        <Transition name="ticker" mode="out-in">
          <span :key="tickerItem.id" class="story__line">
            <span class="story__kind">{{ tickerItem.kindLabel }}</span>{{ tickerItem.title }}
          </span>
        </Transition>
      </span>
      <Transition name="badge">
        <span v-if="unread.length" :key="unread.length" class="story__badge">{{ unread.length }}</span>
      </Transition>
    </button>

    <Teleport to="body">
      <div class="toasts" aria-live="polite">
        <Transition name="toast">
          <article
            v-if="toast"
            :key="toast.id"
            class="toast"
            @mouseenter="pauseToast"
            @mouseleave="resumeToast"
            @focusin="pauseToast"
            @focusout="resumeToast"
          >
            <button type="button" class="toast__open" @click="openFeed(toast.id)">
              <img class="avatar" :src="avatarUrl" alt="" width="192" height="192" />
              <span class="toast__content">
                <span class="toast__meta"><strong>den salte klejne</strong>, {{ ago(toast.publishedAt, now) }}</span>
                <span class="toast__title">{{ toast.title }}</span>
                <span v-if="toast.body" class="toast__text">{{ toast.body }}</span>
              </span>
            </button>
            <button type="button" class="toast__close" aria-label="Luk notifikation" @click="dismissToast">
              <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8" /></svg>
            </button>
            <span
              class="toast__timer"
              :style="{ animationDuration: `${TOAST_MS}ms`, animationPlayState: toastPaused ? 'paused' : 'running' }"
            />
          </article>
        </Transition>
      </div>

      <Transition name="drawer">
        <div v-if="drawerOpen" class="drawer" role="dialog" aria-modal="true" aria-labelledby="drawer-title">
          <div class="drawer__backdrop" @click="closeFeed" />
          <section class="drawer__panel">
            <header class="drawer__head">
              <div>
                <p class="drawer__eyebrow">Opslag</p>
                <h2 id="drawer-title" class="drawer__title">Nyt fra bageriet</h2>
              </div>
              <button ref="closeButton" type="button" class="drawer__close" @click="closeFeed">Luk</button>
            </header>
            <ol class="feed">
              <li
                v-for="(item, index) in items"
                :id="`feed-${item.id}`"
                :key="item.id"
                class="feed__item"
                :class="{ 'is-unread': unreadWhenOpened.has(item.id), 'is-highlight': item.id === highlightId }"
                :style="{ '--i': index }"
              >
                <img class="avatar" :src="avatarUrl" alt="" width="192" height="192" />
                <div class="feed__content">
                  <p class="feed__meta">
                    <strong>den salte klejne</strong>
                    <time :datetime="item.publishedAt">{{ ago(item.publishedAt, now) }}</time>
                    <span v-if="unreadWhenOpened.has(item.id)" class="feed__new">Ny</span>
                  </p>
                  <span class="news__chip" :class="`news__chip--${item.kind}`">{{ item.kindLabel }}</span>
                  <h3 class="feed__title">{{ item.title }}</h3>
                  <p v-if="item.body" class="feed__body">{{ item.body }}</p>
                  <p v-if="item.pinned" class="feed__pinned">Fastgjort opslag</p>
                </div>
              </li>
            </ol>
            <p class="feed__end">Du er helt opdateret</p>
          </section>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>
